/**
 * Click-to-play facade for the VSL player.
 *
 * The poster is a plain <img>, so nothing about the video is fetched on load — this keeps the
 * above-the-fold hero cheap (README performance requirements: nothing above fold autoplays).
 * The real player markup sits inert in a <template> and is swapped in on first activation.
 */
(function () {
  // The script is referenced by several sections; only wire listeners once.
  if (window.__ncsPageScript) return;
  window.__ncsPageScript = true;

  function activate(facade) {
    var tpl = facade.querySelector('template');
    if (!tpl) return;

    var stage = facade.closest('[data-ncs-vsl]');
    if (!stage) return;

    stage.replaceChildren(tpl.content.cloneNode(true));

    // Autoplay the freshly-inserted player: the user already expressed intent by clicking.
    var video = stage.querySelector('video');
    if (video) {
      var play = video.play();
      if (play && typeof play.catch === 'function') play.catch(function () {});
    }

    var iframe = stage.querySelector('iframe');
    if (iframe) iframe.focus();
  }

  function bind(root) {
    (root || document).querySelectorAll('[data-ncs-vsl-facade]').forEach(function (facade) {
      if (facade.dataset.ncsBound) return;
      facade.dataset.ncsBound = '1';
      facade.addEventListener('click', function () {
        activate(facade);
      });
    });
  }

  // Proof-loop videos (case studies): play only while in view, pause offscreen. This keeps the
  // videos from all decoding at once and off-screen (README performance requirement).
  var loopObserver;
  function observeLoops(root) {
    var vids = (root || document).querySelectorAll('[data-ncs-loop] video');
    if (!('IntersectionObserver' in window)) {
      vids.forEach(function (v) {
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      });
      return;
    }
    if (!loopObserver) {
      loopObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              var p = e.target.play();
              if (p && p.catch) p.catch(function () {});
            } else {
              e.target.pause();
            }
          });
        },
        { rootMargin: '100px' }
      );
    }
    vids.forEach(function (v) {
      if (v.dataset.ncsObserved) return;
      v.dataset.ncsObserved = '1';
      loopObserver.observe(v);
    });
  }

  // Custom lazy-loaded images: real src/srcset sit in data-src/data-srcset until this element is
  // within `rootMargin` of the viewport. Needed instead of native loading="lazy" because Chrome's
  // lazy-load distance threshold grows on a detected slow connection (so the image still arrives
  // in time) — on this page, one long single-scroll funnel, that generous threshold covers most
  // of the page, so "lazy" images were loading immediately anyway and competing with the real LCP
  // resource for bandwidth under Lighthouse's throttled mobile run (performance pass).
  var lazyObserver;
  function revealLazyImage(img) {
    if (img.dataset.src) {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    }
    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
      img.removeAttribute('data-srcset');
    }
    if (img.dataset.sizes) {
      img.sizes = img.dataset.sizes;
      img.removeAttribute('data-sizes');
    }
    img.removeAttribute('data-ncs-lazy');
  }
  function initLazyImages(root) {
    var imgs = (root || document).querySelectorAll('img[data-ncs-lazy]');
    if (!('IntersectionObserver' in window)) {
      imgs.forEach(revealLazyImage);
      return;
    }
    if (!lazyObserver) {
      lazyObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              revealLazyImage(entry.target);
              lazyObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '600px 0px' }
      );
    }
    imgs.forEach(function (img) {
      lazyObserver.observe(img);
    });
  }

  // Sticky bottom bar: reveal once the visitor has scrolled past the hero, but hide again once the
  // footer is in view so it never covers the footer's own CTA and links.
  function initSticky(root) {
    (root || document).querySelectorAll('[data-ncs-sticky]').forEach(function (bar) {
      if (bar.dataset.ncsStickyBound) return;
      bar.dataset.ncsStickyBound = '1';
      var threshold = parseInt(bar.getAttribute('data-threshold'), 10) || 1200;
      var footerVisible = false;
      var update = function () {
        bar.classList.toggle('is-visible', window.scrollY > threshold && !footerVisible);
      };
      var footer = document.querySelector('.ncs-foot');
      if (footer && 'IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          footerVisible = entries[0].isIntersecting;
          update();
        }).observe(footer);
      }
      window.addEventListener('scroll', update, { passive: true });
      update();
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    bind(document);
    observeLoops(document);
    initSticky(document);
    initLazyImages(document);
  });

  // Re-bind when the theme editor re-renders a section.
  document.addEventListener('shopify:section:load', function (event) {
    bind(event.target);
    observeLoops(event.target);
    initSticky(event.target);
    initLazyImages(event.target);
  });
})();
